import { get, post, put, del } from '@/lib/api/client'
import {
  AnnouncementFilterParamsSchema,
  PaginationParamsSchema,
  CreateAnnouncementSchema,
  UpdateAnnouncementSchema,
  type Announcement,
  type AnnouncementDetail,
  type AnnouncementFilterParams,
  type CreateAnnouncement,
  type UpdateAnnouncement,
  type ApiResponse,
  type PaginatedResponse,
  type PaginationParams,
  type ContentCategory
} from '@/lib/api/types'

export class AnnouncementService {
  /**
   * Get content categories for announcements
   */
  async getContentCategories(): Promise<ApiResponse<ContentCategory[]>> {
    return await get<ApiResponse<ContentCategory[]>>(
      'announcements/private/content-category'
    )
  }

  /**
   * Get announcements with pagination and filters
   */
  async getAnnouncements(
    params: PaginationParams = { page: 1, size: 10 },
    filters: AnnouncementFilterParams = {}
  ): Promise<ApiResponse<PaginatedResponse<Announcement>>> {
    const validatedParams = PaginationParamsSchema.parse(params)
    const validatedFilters = AnnouncementFilterParamsSchema.parse(filters)

    const query = new URLSearchParams({
      page: validatedParams.page.toString(),
      size: validatedParams.size.toString()
    }).toString()

    return await post<ApiResponse<PaginatedResponse<Announcement>>>(
      `announcements/private/filter?${query}`,
      validatedFilters
    )
  }

  /**
   * Get announcement by ID
   */
  async getAnnouncementById(announcementId: string): Promise<ApiResponse<AnnouncementDetail>> {
    return await get<ApiResponse<AnnouncementDetail>>(
      `announcements/private/${announcementId}`
    )
  }

  /**
   * Create a new announcement
   */
  async createAnnouncement(data: CreateAnnouncement): Promise<ApiResponse<AnnouncementDetail>> {
    const validatedData = CreateAnnouncementSchema.parse(data)

    return await post<ApiResponse<AnnouncementDetail>>(
      'announcements/admin/private',
      validatedData
    )
  }

  /**
   * Update an existing announcement
   */
  async updateAnnouncement(
    announcementId: string,
    data: Partial<UpdateAnnouncement>
  ): Promise<ApiResponse<AnnouncementDetail>> {
    const validatedData = UpdateAnnouncementSchema.partial().parse(data)

    return await put<ApiResponse<AnnouncementDetail>>(
      `announcements/admin/private/${announcementId}`,
      validatedData
    )
  }

  /**
   * Delete an announcement
   */
  async deleteAnnouncement(announcementId: string): Promise<ApiResponse<null>> {
    return await del<ApiResponse<null>>(
      `announcements/admin/private/${announcementId}`
    )
  }

  /**
   * Highlight an announcement
   */
  async highlightAnnouncement(
    announcementId: string,
    data: { highlight: boolean }
  ): Promise<ApiResponse<AnnouncementDetail>> {
    return await put<ApiResponse<AnnouncementDetail>>(
      `announcements/admin/private/${announcementId}/highlight`,
      data
    )
  }
}

export const announcementService = new AnnouncementService()
export default announcementService

